/* eslint-disable max-lines-per-function */
import { useState, useEffect } from 'react';
import { ADD_EDIT_RESPONSE_MESSAGES } from '../../Constants/strings';
import UseAxios from '../../Api/http/http';
import {
  addCertificationData,
  editCertificationData,
} from '../../Api/request/certification';
import { logError } from '../../Utils/logger';

const useCertificationForm = ({
  isEdit,
  editProviderValue,
  editName,
  editProvider,
  editValidity,
  editCertificationId,
  initialCertificationName,
  editId,
  setRows,
  handleClose,
  categoryList,
  editCategoryName,
  editSubCategoryName,
  lastUpdatedBy,
  editCategoryId,
  editSubcategoryId,
}) => {
  const setCategory = (categoryName) => {
    let categoryInfo = categoryList.find(
      (category) => category.name === categoryName,
    );

    if (isEdit && !categoryInfo) {
      categoryInfo = {
        id: editCategoryId,
        name: categoryName,
      };
    }

    return categoryInfo;
  };

  const setSubCategory = (categoryName, subCategoryName) => {
    const categoryDetails = categoryList.find(
      (category) => category.name === categoryName,
    );

    let subCategory = categoryDetails?.subCategories.find(
      (subCategoryInfo) => subCategoryInfo.name === subCategoryName,
    );

    if (isEdit && !subCategory) {
      subCategory = {
        id: editSubcategoryId,
        name: subCategoryName,
      };
    }

    return subCategory;
  };

  const setSubCategoryList = (categoryName) => {
    const categoryData = categoryList.find(
      (category) => category.name === categoryName,
    );
    return categoryData ? categoryData.subCategories : [];
  };

  const [formData, setFormData] = useState({
    providerValue: '',
    nameValue: initialCertificationName,
    providerName: '',
    validity: '',
    certificationID: '',
    categoryValue: editCategoryName || null,
    subCategoryValue: editSubCategoryName || null,
    subCategoryList: editCategoryName
      ? setSubCategoryList(editCategoryName)
      : null,
  });

  const [errors, setErrors] = useState({
    providerIdError: '',
    nameError: '',
    providerNameError: '',
    validityYearError: '',
    categoryError: '',
    subCategoryError: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: '',
  });

  useEffect(() => {
    if (isEdit) {
      setFormData({
        providerValue: editProviderValue,
        nameValue: editName,
        providerName: editProvider,
        validity: (editValidity === null || editValidity === 'N/A') ? '' : editValidity || '',
        certificationID: editCertificationId,
        categoryValue: setCategory(editCategoryName),
        subCategoryValue: setSubCategory(editCategoryName, editSubCategoryName),
        subCategoryList: setSubCategoryList(editCategoryName),
      });
    }
  }, [
    isEdit,
    editProviderValue,
    editName,
    editProvider,
    editValidity,
    editCertificationId,
    categoryList,
    editCategoryName,
    editSubCategoryName,
  ]);

  const resetValues = () => {
    setFormData({
      providerValue: '',
      nameValue: '',
      providerName: '',
      validity: '',
      certificationID: '',
      categoryValue: null,
      subCategoryValue: null,
    });
    setErrors({
      providerIdError: '',
      nameError: '',
      providerNameError: '',
      validityYearError: '',
      categoryError: '',
      subCategoryError: '',
    });
  };

  const snackbarOpen = (message, severity) => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar({ ...snackbar, open: false });
    }, 4000);
  };

  const submitAction = async (data) => {
    const certificationData = {
      providerID: data.providerID,
      certificationName: data.certificationName,
      certificationProvider: data.certificationProvider,
      validityYear: data.validityYear,
      categoryId: data.categoryId,
      subCategoryId: data.subCategoryId,
      lastUpdatedBy: data.lastUpdatedBy,
    };
    const response = isEdit
      ? await UseAxios(
        editCertificationData({ ...certificationData, certificationID: editId }),
      )
      : await UseAxios(
        addCertificationData(
          data.providerID,
          data.certificationName,
          data.certificationProvider,
          data.validityYear,
          data.categoryId,
          data.subCategoryId,
          data.lastUpdatedBy,
        ),
      );

    return response;
  };

  const handleSubmit = async () => {
    const {
      providerValue,
      nameValue,
      providerName,
      validity,
      categoryValue,
      subCategoryValue,
    } = formData;

    const isCategoryValid
      = formData.categoryValue && typeof formData.categoryValue === 'object';
    const isSubCategoryValid
      = formData.subCategoryValue
      && typeof formData.subCategoryValue === 'object';

    const {
      providerIdError,
      nameError,
      providerNameError,
      validityYearError,
      categoryError,
      subCategoryError,
    } = errors;

    if (
      !nameValue.trim()
      || !providerName.trim()
      || !categoryValue
      || !subCategoryValue
    ) {
      setErrors({
        ...errors,
        nameError: !nameValue.trim() ? 'Please fill the required field' : '',
        providerNameError: !providerName.trim()
          ? 'Please fill the required field'
          : '',
        categoryError: !isCategoryValid ? 'Please fill the required field' : '',
        subCategoryError: !isSubCategoryValid
          ? 'Please fill the required field'
          : '',
      });
      return;
    }

    if (
      providerIdError
      || nameError
      || providerNameError
      || validityYearError
      || categoryError
      || subCategoryError
    ) {
      logError('Error:', 'Validation error', errors);
      return;
    }

    try {
      const submitData = {
        providerID: providerValue,
        certificationName: nameValue,
        certificationProvider: providerName,
        validityYear: (validity && validity.trim() !== '') ? Number(validity) : null,
        categoryId: categoryValue.id,
        subCategoryId: subCategoryValue.id,
        lastUpdatedBy: lastUpdatedBy,
      };

      if (isEdit) {
        submitData.certificationID = editId;
      }

      const response = await submitAction(submitData);
      // Check if response contains an error
      if (response?.data instanceof Error || response?.data?.isAxiosError) {
        throw response.data;
      }

      // Check if response contains an error
      if (response?.data instanceof Error || response?.data?.isAxiosError) {
        throw response.data;
      }

      if (
        response?.data?.status === 201
        || response?.data?.status === 200
        || response?.status === 200
        || response?.status === 201
      ) {
        setRows((prevRows) =>
          isEdit
            ? prevRows.map((row) =>
              row.id === editId
                ? {
                  ...row,
                  id: editId,
                  providerId: providerValue,
                  certificationName: nameValue,
                  providerName,
                  validity: (validity && validity.trim() !== '') ? Number(validity) : null,
                  categoryId: categoryValue.id,
                  categoryName: categoryValue.name,
                  subCategoryId: subCategoryValue.id,
                  subCategoryName: subCategoryValue.name,
                }
                : row,
            )
            : [
              ...prevRows,
              {
                id: response.data?.certificationId || response?.certificationId,
                providerID: providerValue,
                certificationName: nameValue,
                certificationProvider: providerName,
                validityYear: (validity && validity.trim() !== '') ? Number(validity) : null,
                categoryId: categoryValue.id,
                categoryName: categoryValue.name,
                subCategoryId: subCategoryValue.id,
                subCategoryName: subCategoryValue.name,
              },
            ],
        );
        snackbarOpen(
          isEdit
            ? ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_UPDATED_SUCCESS
            : ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_ADDED_SUCCESS,
          'success',
        );
        resetValues();
        handleClose();
      } else {

        throw new Error(`Unexpected response status: ${response?.data?.status || response?.status}`);
      }
    } catch (error) {
      let errorMessage = '';


      // Check if this is actually a successful response that was thrown as an error
      if (error?.response?.status === 500 || error?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error?.response?.status === 400) {
        errorMessage = error.response.data?.externalMessage || 'Bad request error';
      } else if (error?.response?.status === 409) {
        errorMessage
          = ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_CONFLICT
          || error.response.data?.externalMessage || 'Conflict error';
      } else if (error?.status === 400) {
        errorMessage = error.data?.externalMessage || 'Bad request error';
      } else if (error?.status === 409) {
        errorMessage
          = ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_CONFLICT
          || error.data?.externalMessage || 'Conflict error';
      } else if (isEdit) {
        errorMessage = ADD_EDIT_RESPONSE_MESSAGES.OOPS_UPDATE_TRY_AGAIN;
      } else {
        errorMessage = ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN;
      }

      if (errorMessage) {
        snackbarOpen(errorMessage, 'warning');
      }
      logError('Error:', error);
      // Don't reset values and close on error - let user fix the issue
      // resetValues();
      // handleClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    if (field === 'providerValue') {
      if (value.length < 1 || value.length > 20) {
        setErrors({
          ...errors,
          providerIdError: ADD_EDIT_RESPONSE_MESSAGES.PROVIDER_ID_MUST_BE,
        });
      } else if (!/^[a-zA-Z0-9]+$/.test(value)) {
        setErrors({
          ...errors,
          providerIdError: ADD_EDIT_RESPONSE_MESSAGES.PROVIDER_ID_INVALID,
        });
      } else {
        setErrors({ ...errors, providerIdError: '' });
      }
    } else if (field === 'nameValue' || field === 'providerName') {
      const errorType = getErrorType(value);
      const errorMessage
        = field === 'nameValue'
          ? ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_NAME_MUST_BE
          : ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_PROVIDER_MUST_BE;

      setErrors({
        ...errors,
        [`${field}Error`]:
          errorType === 'LENGTH_EXCEEDED' || errorType === 'INVALID_LENGTH'
            ? errorMessage
            : errorType === 'INVALID_CHARACTERS'
              ? ADD_EDIT_RESPONSE_MESSAGES.CERTIFICATION_NAME_INVALID
              : '',
      });
    } else if (field === 'validity') {
      if (value && value.length > 3) {
        setErrors({
          ...errors,
          validityYearError: ADD_EDIT_RESPONSE_MESSAGES.VALIDITY_MUST_BE,
        });
      } else if (
        value
        && value !== ''
        && value !== null
        && value !== 'N/A'
        && (!/^\d+(\.\d*)?$/.test(value) || value === '0.0')
      ) {
        setErrors({
          ...errors,
          validityYearError: ADD_EDIT_RESPONSE_MESSAGES.VALIDITY_INVALID,
        });
      } else {
        setErrors({ ...errors, validityYearError: '' });
      }
    } else if (field === 'categoryValue') {
      const oldValue = formData?.categoryValue?.id;
      const newValue = value.id;
      if (oldValue !== newValue) {
        setFormData({
          ...formData,
          categoryValue: value,
          subCategoryValue: null,
          subCategoryList: setSubCategoryList(value.name),
        });
      }
      setErrors({
        ...errors,
        categoryError: '',
      });
    } else if (field === 'subCategoryValue') {
      setFormData({
        ...formData,
        subCategoryValue: setSubCategory(
          formData.categoryValue.name,
          value.name,
        ),
      });
      setErrors({
        ...errors,
        subCategoryError: '',
      });
    }
  };

  const getErrorType = (value) => {
    const alphanumericLength = value.replace(/\s+/g, '').length;

    if (value.length > 50) {
      return 'LENGTH_EXCEEDED';
    } else if (alphanumericLength < 3 || alphanumericLength > 50) {
      return 'INVALID_LENGTH';
    } else if (!/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/.test(value)) {
      return 'INVALID_CHARACTERS';
    } else {
      return 'NO_ERROR';
    }
  };

  return {
    formData,
    errors,
    snackbar,
    handleInputChange,
    handleSubmit,
    resetValues,
  };
};

export default useCertificationForm;
